import { Content } from "@iventis/translations";
import { translate } from "@iventis/translations/translation";

/**
 * Formats a timestamp into a user-friendly, relative string.
 * Examples:
 * - "Today at 3:15 PM"
 * - "Yesterday at 8:42 AM"
 * - "Tuesday at 11:30 AM"
 * - "27 Feb at 9:05 AM"
 * - "27 Feb 2025 at 9:05 AM"
 */
export const formatLocalTime = (value: Date): string => {
    const date = new Date(value);
    const now = new Date();

    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfGivenDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const msPerDay = 24 * 60 * 60 * 1000;
    const dayDiff = Math.floor((startOfToday.getTime() - startOfGivenDay.getTime()) / msPerDay);

    const timePart = new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    }).format(date);
    if (dayDiff === 0) return translate(Content.changeLog.dateTimes.todayAt, { time: timePart });
    if (dayDiff === 1) return translate(Content.changeLog.dateTimes.yesterdayAt, { time: timePart });

    if (dayDiff > 1 && dayDiff < 7) {
        const dayName = getTranslateDay(date);
        return translate(Content.changeLog.dateTimes.dayAt, { day: dayName, time: timePart });
    }

    if (date.getFullYear() === now.getFullYear()) {
        const dayMonth = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short" }).format(date);
        return `${dayMonth} at ${timePart}`;
    }

    const dayMonthYear = new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    }).format(date);
    return translate(Content.changeLog.dateTimes.dayAt, { day: dayMonthYear, time: timePart });
};

export const getDateHeader = (value: Date): string => {
    const date = new Date(value);
    const now = new Date();

    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfGivenDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const msPerDay = 24 * 60 * 60 * 1000;
    const dayDiff = Math.floor((startOfToday.getTime() - startOfGivenDay.getTime()) / msPerDay);

    if (dayDiff <= 0) return translate(Content.changeLog.dateTimes.today);
    if (dayDiff === 1) return translate(Content.changeLog.dateTimes.yesterday);
    if (dayDiff < 7) {
        return getTranslateDay(date);
    }
    return translate(Content.changeLog.dateTimes.aWeekAgo);
};

const getTranslateDay = (date: Date) => {
    const dayNumber = date.getDay();
    switch (dayNumber) {
        case 0:
            return translate(Content.changeLog.dateTimes.weekday0);
        case 1:
            return translate(Content.changeLog.dateTimes.weekday1);
        case 2:
            return translate(Content.changeLog.dateTimes.weekday2);
        case 3:
            return translate(Content.changeLog.dateTimes.weekday3);
        case 4:
            return translate(Content.changeLog.dateTimes.weekday4);
        case 5:
            return translate(Content.changeLog.dateTimes.weekday5);
        case 6:
            return translate(Content.changeLog.dateTimes.weekday6);
        default:
            return "unkown date";
    }
};
